import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { requestPasswordReset } from "../api/account";
import { Button } from "@t-one-internal/design-system";
import { Input } from "@t-one-internal/design-system";
import { Label } from "@t-one-internal/design-system";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@t-one-internal/design-system";
import { Mail, CheckCircle } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromLogin = location.state?.email || "";

  const [email, setEmail] = useState(emailFromLogin);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(!emailFromLogin);

  // Update email if it comes from login page
  useEffect(() => {
    if (emailFromLogin) {
      setEmail(emailFromLogin);
      setShowManualEntry(false);
    }
  }, [emailFromLogin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    setIsSubmitting(true);

    try {
      await requestPasswordReset({ email });
      setSuccessMessage(
        "If an account exists with this email, you will receive a password reset link shortly. Please check your inbox."
      );
      // Don't clear email so user can retry if needed
    } catch (error: any) {
      console.error("Error requesting password reset:", error);
      // For security, we show a generic message even if the email doesn't exist
      setSuccessMessage(
        "If an account exists with this email, you will receive a password reset link shortly. Please check your inbox."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success screen after sending reset link
  if (successMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-violet-100 p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="space-y-1 pb-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-xl bg-green-500 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center text-gray-900">Check Your Email</CardTitle>
            <CardDescription className="text-center text-gray-600">
              We've sent a password reset link to <span className="font-semibold text-gray-900">{email}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
              {successMessage}
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => navigate("/login")}
                className="w-full h-11"
                style={{ backgroundColor: '#4B45E5' }}
              >
                Back to Login
              </Button>

              <button
                onClick={() => {
                  setSuccessMessage("");
                  setError("");
                  // Keep showManualEntry false if we have an email
                  if (email) {
                    setShowManualEntry(false);
                  }
                }}
                className="w-full text-sm text-gray-600 hover:text-gray-900 py-2"
              >
                Didn't receive the email? Try again
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-violet-100 p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="space-y-1 pb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#4B45E5' }}>
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center text-gray-900">Reset Password</CardTitle>
          <CardDescription className="text-center text-gray-600">
            {!showManualEntry && email
              ? "We'll send a password reset link to your email"
              : "Enter your email address and we'll send you a link to reset your password"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Confirmation View (when coming from login page or after "Try again") */}
            {!showManualEntry && email ? (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-blue-900 mb-1">Sending reset link to:</p>
                      <p className="text-sm font-semibold text-blue-700 truncate">{email}</p>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11"
                  style={{ backgroundColor: '#4B45E5' }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending Reset Link..." : "Send Reset Link"}
                </Button>

                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="w-full text-sm text-gray-600 hover:text-gray-900 py-2"
                >
                  Back to Login
                </button>
              </div>
            ) : (
              /* Manual Email Entry View (fallback) */
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                    className="h-11"
                    required
                    disabled={isSubmitting}
                    autoFocus
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-11"
                  style={{ backgroundColor: '#4B45E5' }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send Reset Link"}
                </Button>

                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="w-full text-sm text-gray-600 hover:text-gray-900 py-2"
                >
                  Back to Login
                </button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
